/*
 * Project: bellcloud
 * File: packages/theme/tokens/colors.ts
 * Description: Color tokens based on Bellcloud Design System
 */

export type ThemeMode = 'light' | 'dark';

// Base color tokens
export const baseColors = {
  neutral: {
    0: '#FFFFFF',
    2: '#FBFBFB',
    5: '#F1F1F1',
    10: '#E8E8E8',
    20: '#DDDDDD',
    40: '#A5A5A5',
    60: '#6C6C6C',
    80: '#565656',
    85: '#3F3F3F',
    90: '#343434',
    95: '#282828',
    100: '#1D1D1D',
  },
  brand: {
    5: '#F8F5FF',
    10: '#F0EBFE',
    20: '#E1D8FE',
    40: '#C3B1FD',
    60: '#A68AFC',
    80: '#8863FB',
    100: '#6A3CFA',
    200: '#4B20D3',
    1000: '#3F355C',
    1200: '#2F2450',
  },
  chromatic: {
    red: {
      // 10: '#FAE8E8',
      30: '#F8C4C4',
      100: '#E73A3A',
    },
    orange: {
      30: '#F9E1D2',
      100: '#E3671F',
    },
    green: {
      // 10: '#E9F6E9',
      30: '#C9E6C4',
      100: '#4CAC39',
    },
    blue: {
      30: '#CCE9FB',
      100: '#0091EA',
    },
  },
  dim: {
    light: {
      black: 'rgba(0, 0, 0, 0.05)',
      blackMedium: 'rgba(0, 0, 0, 0.1)',
    },
    dark: {
      white: 'rgba(255, 255, 255, 0.05)',
      whiteMedium: 'rgba(255, 255, 255, 0.1)',
    },
  },
};

// Semantic color tokens
export type ColorTokens = {
  // Text colors
  text: {
    primary: string;
    secondary: string;
    emphasis: string;
    disabled: string;
    inverse: string;
  };

  // Button colors
  button: {
    text: {
      primary: string;
      secondary: string;
      tertiaryBrand: string;
      tertiaryNeutral: string;
      disabled: string;
    };
    bg: {
      primary: {
        default: string;
        hover: string;
      };
      secondary: {
        default: string;
        hover: string;
      };
      disabled: string;
    };
    stroke: {
      tertiaryBrand: {
        default: string;
        hover: string;
      };
      tertiaryNeutral: {
        default: string;
        hover: string;
      };
    };
  };

  // Logo colors
  logo: {
    neutral: string;
    inverse: string;
    brand: string;
  };

  // Control colors
  control: {
    tolltip: { default: string };
    toggle: {
      on: string;
      off: string;
      disabled: string;
      thumb: string;
    };
    scroll: {
      gutter: string;
      handgrip: string;
    };
    timetrack: {
      bg: string;
      progress: string;
      thumb: string;
    };
    splitBar: {
      default: string;
      select: string;
    };
    inputUnderline: {
      default: string;
    };
  };

  // Status colors
  status: {
    danger: {
      stroke: {
        border: string;
        surface: string;
        icon: string;
        text: string;
      };
      fill: {
        surface: string;
        icon: string;
        text: string;
      };
    };
    caution: {
      stroke: {
        border: string;
        icon: string;
        text: string;
      };
      fill: {
        surface: string;
        icon: string;
        text: string;
      };
    };
    stable: {
      stroke: {
        border: string;
        surface: string;
        icon: string;
        text: string;
      };
      fill: {
        surface: string;
        icon: string;
        text: string;
      };
    };
    calib: {
      stroke: {
        border: string;
        surface: string;
        icon: string;
        text: string;
      };
      fill: {
        surface: string;
        icon: string;
        text: string;
      };
    };
  };

  // Surface colors
  surface: {
    layout: {
      background: string;
      container: string;
      overlay: string;
      summary: string;
    };

    group: {
      default: string;
      brand: string;
    };
    header: {
      default: string;
      nested: string;
    };
    control: {
      default: string;
      emphasis: string;
      disabled: string;
    };
    interactive: {
      hover: string;
      selectedNav: string;
      selectedContent: string;
    };
    feedback: {
      helper: string;
      error: string;
      success: string;
      neutral: string;
      /** caution / warning surfaces (toast, inline alerts) */
      warning: string;
    };
    dim: {
      light: string;
      medium: string;
    };
  };

  // Border colors
  border: {
    container: string;
    divider: string;
    indicator: string;
    group: {
      default: string;
      brand: string;
    };
    interactive: {
      default: string;
      hover: string;
      selected: string;
    };
    feedback: {
      helper: string;
      error: string;
    };
    shadow: {
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
  };

  // Icon colors
  icon: {
    primary: string;
    secondary: string;
    emphasis: string;
    disabled: string;
    inverse: string;
  };
};

export const lightColors: ColorTokens = {
  text: {
    primary: baseColors.neutral[100], // #1D1D1D
    secondary: baseColors.neutral[60], // #6C6C6C
    emphasis: baseColors.brand[200], // #4B20D3
    disabled: baseColors.neutral[40], // #A5A5A5
    inverse: baseColors.neutral[0], // #FFFFFF
  },

  button: {
    text: {
      primary: baseColors.neutral[0], // #FFFFFF
      secondary: baseColors.neutral[0], // #FFFFFF
      tertiaryBrand: baseColors.brand[200], // #4B20D3
      tertiaryNeutral: baseColors.neutral[100], // #1D1D1D
      disabled: baseColors.neutral[2], // #FBFBFB
    },
    bg: {
      primary: {
        default: baseColors.brand[100], // #6A3CFA
        hover: baseColors.brand[80], // #8863FB
      },
      secondary: {
        default: baseColors.neutral[80], // #565656
        hover: baseColors.neutral[60], // #6C6C6C
      },
      disabled: baseColors.neutral[20], // #DDDDDD
    },
    stroke: {
      tertiaryBrand: {
        default: baseColors.brand[200], // #4B20D3
        hover: baseColors.brand[80], // #8863FB
      },
      tertiaryNeutral: {
        default: baseColors.neutral[100], // #1D1D1D
        hover: baseColors.neutral[60], // #6C6C6C
      },
    },
  },
  logo: {
    neutral: baseColors.neutral[100], // #1D1D1D
    inverse: baseColors.neutral[0], // #FFFFFF
    brand: baseColors.brand[100], // #6A3CFA
  },
  control: {
    tolltip: { default: baseColors.neutral[90] }, // #343434
    toggle: {
      on: baseColors.brand[100], // #6A3CFA
      off: baseColors.neutral[20], // #DDDDDD
      disabled: baseColors.neutral[5], // #F1F1F1
      thumb: baseColors.neutral[0], // #FFFFFF
    },
    scroll: {
      gutter: baseColors.neutral[5], // #F1F1F1
      handgrip: baseColors.brand[20], // #E1D8FE
    },
    splitBar: {
      default: baseColors.neutral[20], // #DDDDDD
      select: baseColors.brand[100], // #6A3CFA
    },
    timetrack: {
      bg: baseColors.neutral[20], // #DDDDDD
      progress: baseColors.brand[100], // #6A3CFA
      thumb: baseColors.brand[100], // #6A3CFA
    },

    inputUnderline: {
      default: baseColors.neutral[60], // #6C6C6C
    },
  },

  status: {
    danger: {
      stroke: {
        border: baseColors.chromatic.red[100], // #E73A3A
        surface: baseColors.chromatic.red[30], // #F8C4C4
        icon: baseColors.chromatic.red[100], // #E73A3A
        text: baseColors.chromatic.red[100], // #E73A3A
      },
      fill: {
        surface: baseColors.chromatic.red[100], // #E73A3A
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    caution: {
      stroke: {
        border: baseColors.chromatic.orange[100], // #E3671F
        icon: baseColors.chromatic.orange[100], // #E3671F
        text: baseColors.chromatic.orange[100], // #E3671F
      },
      fill: {
        surface: baseColors.chromatic.orange[100], // #E3671F
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    stable: {
      stroke: {
        border: baseColors.chromatic.green[100], // #4CAC39
        surface: baseColors.chromatic.green[30], // #C9E6C4
        icon: baseColors.chromatic.green[100], // #4CAC39
        text: baseColors.chromatic.green[100], // #4CAC39
      },
      fill: {
        surface: baseColors.chromatic.green[100], // #4CAC39
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    calib: {
      stroke: {
        border: baseColors.neutral[80], // #565656
        surface: baseColors.neutral[5], // #F1F1F1
        icon: baseColors.neutral[80], // #565656
        text: baseColors.neutral[80], // #565656
      },
      fill: {
        surface: baseColors.neutral[60], // #6C6C6C
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
  },

  surface: {
    layout: {
      background: baseColors.neutral[0], // #FFFFFF
      container: baseColors.neutral[2], // #FBFBFB
      overlay: baseColors.neutral[0], // #FFFFFF
      summary: baseColors.brand[10], // #F0EBFE
    },
    group: {
      default: baseColors.neutral[5], // #F1F1F1
      brand: baseColors.brand[20], // #E1D8FE
      // header: baseColors.neutral[5], // #F1F1F1
    },
    header: {
      default: baseColors.neutral[5], // #F1F1F1
      nested: baseColors.brand[5], // #F8F5FF
    },
    control: {
      default: baseColors.neutral[0], // #FFFFFF
      emphasis: baseColors.brand[5], // #F8F5FF
      disabled: baseColors.neutral[5], // #F1F1F1
    },
    interactive: {
      hover: baseColors.brand[5], // #F8F5FF
      selectedNav: baseColors.brand[40], // #C3B1FD
      selectedContent: baseColors.brand[10], // #F0EBFE
    },
    feedback: {
      helper: baseColors.brand[20], // #E1D8FE
      error: baseColors.chromatic.red[30], // #F5D0D0
      success: baseColors.chromatic.green[30], // #D2ECD3
      neutral: baseColors.neutral[10], // #E8E8E8
      warning: baseColors.chromatic.orange[30], // #F9E1D2
    },
    dim: {
      light: baseColors.dim.light.black, // rgba(0, 0, 0, 0.05)
      medium: baseColors.dim.light.blackMedium, // rgba(0, 0, 0, 0.1)
    },
  },

  border: {
    group: {
      default: baseColors.neutral[10], // #E8E8E8
      brand: baseColors.brand[100], // #6A3CFA
    },
    interactive: {
      default: baseColors.neutral[10], // #E8E8E8
      hover: baseColors.brand[20], // #E1D8FE
      selected: baseColors.brand[80], // #8863FB
    },
    feedback: {
      helper: baseColors.brand[100], // #6A3CFA
      error: baseColors.chromatic.red[100], // #CE1515
    },
    shadow: {
      sm: baseColors.dim.light.black, // rgba(0, 0, 0, 0.05)
      md: baseColors.dim.light.blackMedium, // rgba(0, 0, 0, 0.1)
      lg: baseColors.dim.light.blackMedium, // rgba(0, 0, 0, 0.1)
      xl: baseColors.dim.light.blackMedium, // rgba(0, 0, 0, 0.1)
    },
    container: baseColors.neutral[20], // #DDDDDD
    divider: baseColors.neutral[10], // #E8E8E8
    indicator: baseColors.brand[100], // #6A3CFA
  },

  icon: {
    primary: baseColors.neutral[100], // #1D1D1D
    secondary: baseColors.neutral[60], // #6C6C6C
    emphasis: baseColors.brand[100], // #6A3CFA
    disabled: baseColors.neutral[20], // #DDDDDD
    inverse: baseColors.neutral[0], // #FFFFFF
  },
};

// Dark theme - using similar colors with adjusted values for better contrast
export const darkColors: ColorTokens = {
  text: {
    primary: baseColors.neutral[0], // #FFFFFF
    secondary: baseColors.neutral[40], // #A5A5A5
    emphasis: baseColors.brand[10], // #F0EBFE
    disabled: baseColors.neutral[60], // #6C6C6C
    inverse: baseColors.neutral[100], // #1D1D1D
  },

  button: {
    text: {
      primary: baseColors.neutral[0], // #FFFFFF
      secondary: baseColors.neutral[100], // #1D1D1D
      tertiaryBrand: baseColors.brand[20], // #E1D8FE
      tertiaryNeutral: baseColors.neutral[10], // #E8E8E8
      disabled: baseColors.neutral[80], // #565656
    },
    bg: {
      primary: {
        default: baseColors.brand[100], // #6A3CFA
        hover: baseColors.brand[80], // #8863FB
      },
      secondary: {
        default: baseColors.neutral[10], // #E8E8E8
        hover: baseColors.neutral[20], // #DDDDDD
      },
      disabled: baseColors.neutral[90], // #343434
    },
    stroke: {
      tertiaryBrand: {
        default: baseColors.brand[20], // #E1D8FE
        hover: baseColors.brand[40], // #C3B1FD
      },
      tertiaryNeutral: {
        default: baseColors.neutral[20], // #DDDDDD
        hover: baseColors.neutral[40], // #A5A5A5
      },
    },
  },
  logo: {
    neutral: baseColors.neutral[100], // #1D1D1D
    inverse: baseColors.neutral[0], // #FFFFFF
    brand: baseColors.brand[80], // #8863FB
  },
  control: {
    tolltip: { default: baseColors.neutral[5] }, // #F1F1F1
    toggle: {
      on: baseColors.brand[100], // #6A3CFA
      off: baseColors.neutral[80], // #565656
      disabled: baseColors.neutral[90], // #343434
      thumb: baseColors.neutral[10], // #E8E8E8
    },
    scroll: {
      gutter: baseColors.neutral[85], // #3F3F3F
      handgrip: baseColors.neutral[80], // #565656
    },

    splitBar: {
      default: baseColors.neutral[80], // #565656
      select: baseColors.brand[100], // #6A3CFA
    },
    timetrack: {
      bg: baseColors.neutral[80], // #565656
      progress: baseColors.brand[100], // #6A3CFA
      thumb: baseColors.brand[100], // #6A3CFA
    },

    inputUnderline: {
      default: baseColors.neutral[40], // #A5A5A5
    },
  },

  status: {
    danger: {
      stroke: {
        border: baseColors.chromatic.red[100], // #E73A3A
        surface: baseColors.chromatic.red[30], // #F8C4C4
        icon: baseColors.chromatic.red[100], // #E73A3A
        text: baseColors.chromatic.red[100], // #E73A3A
      },
      fill: {
        surface: baseColors.chromatic.red[100], // #E73A3A
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    caution: {
      stroke: {
        border: baseColors.chromatic.orange[100], // #E3671F
        icon: baseColors.chromatic.orange[100], // #E3671F
        text: baseColors.chromatic.orange[100], // #E3671F
      },
      fill: {
        surface: baseColors.chromatic.orange[100], // #E3671F
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    stable: {
      stroke: {
        border: baseColors.chromatic.green[100], // #4CAC39
        surface: baseColors.chromatic.green[30], // #C9E6C4
        icon: baseColors.chromatic.green[100], // #4CAC39
        text: baseColors.chromatic.green[100], // #4CAC39
      },
      fill: {
        surface: baseColors.chromatic.green[100], // #4CAC39
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
    calib: {
      stroke: {
        border: baseColors.neutral[80], // #565656
        surface: baseColors.neutral[5], // #F1F1F1
        icon: baseColors.neutral[80], // #565656
        text: baseColors.neutral[80], // #565656
      },
      fill: {
        surface: baseColors.neutral[60], // #6C6C6C
        icon: baseColors.neutral[0], // #FFFFFF
        text: baseColors.neutral[0], // #FFFFFF
      },
    },
  },

  surface: {
    layout: {
      background: baseColors.neutral[100], // #1D1D1D
      container: baseColors.neutral[95], // #282828
      overlay: baseColors.neutral[100], // #1D1D1D
      summary: baseColors.brand[1000], // #3F355C
    },
    group: {
      default: baseColors.neutral[85], // #3F3F3F
      brand: baseColors.brand[60], // #A68AFC
      // header: baseColors.neutral[85], // #3F3F3F
    },
    header: {
      default: baseColors.neutral[85], // #3F3F3F
      nested: baseColors.brand[1200], // #2F2450
    },
    control: {
      default: baseColors.neutral[100], // #1D1D1D
      emphasis: baseColors.neutral[95], // #282828
      disabled: baseColors.neutral[90], // #343434
    },
    interactive: {
      hover: baseColors.neutral[90], // #343434
      selectedNav: baseColors.brand[80], // #8863FB
      selectedContent: baseColors.brand[1000], // #A68AFC
    },
    feedback: {
      helper: baseColors.brand[100], // #6A3CFA
      error: baseColors.chromatic.red[100], // #CE1515
      success: baseColors.chromatic.green[30], // #D2ECD3
      neutral: baseColors.neutral[10], // #E8E8E8
      warning: baseColors.chromatic.orange[100], // #E3671F
    },
    dim: {
      light: baseColors.dim.dark.white, // rgba(255, 255, 255, 0.05)
      medium: baseColors.dim.dark.whiteMedium, // rgba(255, 255, 255, 0.1)
    },
  },

  border: {
    group: {
      default: baseColors.neutral[80], // #565656
      brand: baseColors.brand[100], // #6A3CFA
    },
    interactive: {
      default: baseColors.neutral[80], // #565656
      hover: baseColors.neutral[60], // #6C6C6C
      selected: baseColors.brand[80], // #8863FB
    },
    feedback: {
      helper: baseColors.brand[100], // #6A3CFA
      error: baseColors.chromatic.red[100], // #CE1515
    },
    shadow: {
      sm: baseColors.dim.dark.white, // rgba(255, 255, 255, 0.05)
      md: baseColors.dim.dark.whiteMedium, // rgba(255, 255, 255, 0.1)
      lg: baseColors.dim.dark.whiteMedium, // rgba(255, 255, 255, 0.1)
      xl: baseColors.dim.dark.whiteMedium, // rgba(255, 255, 255, 0.1)
    },
    container: baseColors.neutral[80], // #565656
    divider: baseColors.neutral[80], // #565656
    indicator: baseColors.brand[80], // #8863FB
  },

  icon: {
    primary: baseColors.neutral[0], // #FFFFFF
    secondary: baseColors.neutral[40], // #A5A5A5
    emphasis: baseColors.brand[100], // #6A3CFA
    disabled: baseColors.neutral[20], // #DDDDDD
    inverse: baseColors.neutral[100], // #1D1D1D
  },
};
